import React, { useEffect, useMemo, useState } from 'react';

import StudyViewer, { ViewerSource } from '../components/StudyViewer';
import partogradMark from '../assets/partograd-mark.png';
import { isolate } from '../utils/format';
import { sharedCase, shareSource } from '../utils/share';

/*
 * صفحه‌ای که پزشک یا بیمار با لینک پیامک‌شده باز می‌کند.
 *
 * بیرونِ AuthProvider و SocketProvider می‌نشیند: هیچ حسابی در کار نیست و
 * WebSocketِ سامانه هم نباید برایش باز شود. App.js پیش از هر چیز دیگری
 * نشانی را می‌بیند و اگر `/v/<token>` بود مستقیم همین را نشان می‌دهد.
 *
 * **زبان از مخاطب می‌آید.** صفحه‌ی پزشک انگلیسی است چون کنار گزارشِ
 * انگلیسی خوانده می‌شود؛ صفحه‌ی بیمار فارسی. خودِ نمایشگر در هر دو حالت
 * انگلیسی می‌ماند — واژه‌های اگزیال و میلی‌متر روی تصویر ترجمه نمی‌خواهند
 * و ترجمه‌شان فقط پزشک را گیج می‌کند.
 */

const TEXT = {
  doctor: {
    dir: 'ltr',
    lang: 'en',
    brand: 'FlowTrack',
    clinic: 'Partograd Dental & Maxillofacial Radiology',
    loading: 'Loading…',
    gone: 'This link is no longer valid. It may have expired or been revoked.',
    failed: 'Could not load this page. Please check your connection and try again.',
    images: 'Images',
    report: 'Report',
    referredBy: 'Referred by',
    order: 'Order',
    received: 'Received',
    noReport: 'The report has not been finalised yet. Please check back later.',
    noImages: 'No images are available for viewing on this link.',
    imagesPending:
      'The images are still being prepared for viewing. This usually takes a few minutes.',
    validUntil: 'Link valid until',
    title: 'CBCT — {name}',
  },
  patient: {
    dir: 'rtl',
    lang: 'fa',
    brand: 'FlowTrack',
    clinic: 'مرکز تخصصی رادیولوژی و سی‌تی‌اسکن دهان، فک و صورت',
    loading: 'در حال بارگذاری…',
    gone: 'این لینک دیگر معتبر نیست. ممکن است منقضی شده باشد.',
    failed: 'صفحه بارگذاری نشد. اتصال اینترنت را بررسی کنید و دوباره امتحان کنید.',
    images: 'تصاویر',
    report: 'گزارش',
    referredBy: 'پزشک معالج',
    order: 'شماره پرونده',
    received: 'تاریخ پذیرش',
    noReport: 'گزارش هنوز نهایی نشده است. کمی بعد دوباره سر بزنید.',
    noImages: 'تصویری برای نمایش روی این لینک وجود ندارد.',
    imagesPending: 'تصاویر در حال آماده‌سازی برای نمایش‌اند. معمولاً چند دقیقه طول می‌کشد.',
    validUntil: 'اعتبار لینک تا',
    title: 'تصاویر و گزارش — {name}',
  },
};

function formatDate(value, lang) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(
      lang === 'fa' ? 'fa-IR' : 'en-GB',
      { year: 'numeric', month: 'long', day: 'numeric' },
    );
  } catch {
    return '';
  }
}

export default function SharedStudy({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('images');

  const source = useMemo(() => shareSource(token), [token]);

  useEffect(() => {
    let alive = true;
    sharedCase(token)
      .then((response) => alive && setData(response.data))
      .catch((err) => {
        if (!alive) return;
        // ۴۰۴ یعنی «منقضی یا باطل»، هر چیز دیگری یعنی مشکل شبکه. تفکیکشان
        // مهم است: اولی را پزشک باید به کلینیک بگوید، دومی را خودش حل کند.
        setError(err?.response?.status === 404 ? 'gone' : 'failed');
      });
    return () => { alive = false; };
  }, [token]);

  /*
   * عنوان تب.
   *
   * index.html عنوان فارسیِ داخلی سامانه را دارد و پزشک نباید آن را
   * ببیند — روی گوشی، عنوانِ تب همان چیزی است که در فهرست تب‌ها و در
   * اشتراک‌گذاری دیده می‌شود.
   */
  useEffect(() => {
    if (!data) return undefined;
    const t = TEXT[data.audience] || TEXT.patient;
    const previous = document.title;
    document.title = t.title.replace('{name}', isolate(data.case.name));
    document.documentElement.lang = t.lang;
    return () => { document.title = previous; };
  }, [data]);

  // تا وقتی مخاطب معلوم نشده، زبان صفحه هم معلوم نیست. حالتِ بارگذاری
  // عمداً بی‌طرف است و فقط یک نشانه‌ی چرخان دارد.
  if (error) {
    const t = TEXT[data?.audience] || TEXT.patient;
    return (
      <div className="pub" dir={t.dir} lang={t.lang}>
        <div className="pub__card pub__card--message">
          <img className="pub__mark" src={partogradMark} alt="Partograd" />
          <p>{t[error]}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="pub" dir="rtl">
        <div className="pub__card pub__card--message">
          <img className="pub__mark pub__mark--spin" src={partogradMark} alt="Partograd" />
          <p>…</p>
        </div>
      </div>
    );
  }

  const t = TEXT[data.audience] || TEXT.patient;
  const study = data.studies[0] || null;

  return (
    <div className="pub" dir={t.dir} lang={t.lang}>
      <header className="pub__head">
        {/* نشان پرتوگراد همیشه بالا سمت چپ می‌نشیند — در صفحه‌ی فارسی هم.
            برای همین `pub__logo` با `position: absolute` و `left` مطلق
            جا می‌گیرد، نه با `margin-inline-start` که در RTL برعکس
            می‌شود. */}
        <img className="pub__logo" src={partogradMark} alt="Partograd" />
        <span className="pub__brand">{t.brand}</span>
        <span className="pub__clinic">{t.clinic}</span>
        <h1 className="pub__name"><bdi>{data.case.name}</bdi></h1>
        <dl className="pub__meta">
          <div>
            <dt>{t.order}</dt>
            <dd dir="ltr">{data.case.order_number}</dd>
          </div>
          <div>
            <dt>{t.referredBy}</dt>
            <dd><bdi>{data.case.doctor}</bdi></dd>
          </div>
          <div>
            <dt>{t.received}</dt>
            <dd>{formatDate(data.case.reception_time, t.lang)}</dd>
          </div>
        </dl>
      </header>

      {/* روی موبایل یکی از دو تا، روی نمایشگر بزرگ هر دو کنار هم. تب‌ها
          فقط وقتی معنی دارند که هر دو تکه چیزی برای نشان دادن داشته باشند. */}
      <div className="pub__tabs" role="tablist">
        <button
          type="button" role="tab"
          aria-selected={tab === 'images'}
          className={`pub__tab ${tab === 'images' ? 'is-on' : ''}`}
          onClick={() => setTab('images')}
        >
          {t.images}
        </button>
        <button
          type="button" role="tab"
          aria-selected={tab === 'report'}
          className={`pub__tab ${tab === 'report' ? 'is-on' : ''}`}
          onClick={() => setTab('report')}
        >
          {t.report}
        </button>
      </div>

      <div className="pub__body">
        <section className={`pub__pane pub__pane--images ${tab === 'images' ? 'is-on' : ''}`}>
          {study ? (
            <ViewerSource.Provider value={source}>
              <StudyViewer studyId={study.id} />
            </ViewerSource.Provider>
          ) : (
            <p className="pub__empty">
              {data.images_pending ? t.imagesPending : t.noImages}
            </p>
          )}
        </section>

        <section className={`pub__pane pub__pane--report ${tab === 'report' ? 'is-on' : ''}`}>
          {data.report_text ? (
            // گزارش انگلیسی است و با فاصله‌های معنادار رندر شده؛ pre آن را
            // همان‌طور که سرور ساخته نگه می‌دارد.
            <pre className="pub__report" dir="ltr">{data.report_text}</pre>
          ) : (
            <p className="pub__empty">{t.noReport}</p>
          )}
        </section>
      </div>

      {/* هشدارِ «اندازه‌ها تقریبی‌اند» اینجا تکرار نمی‌شود — خودِ نمایشگر
          آن را زیر تصویر دارد و دو بار گفتنش فقط بی‌اثرش می‌کند. */}
      <footer className="pub__foot">
        {data.expires_at && (
          <p className="pub__expiry">
            {t.validUntil} {formatDate(data.expires_at, t.lang)}
          </p>
        )}
      </footer>
    </div>
  );
}
